import React from 'react';
import { Button } from '../components/ui/Button';
import { Card, CardBody } from '../components/ui/Card';
import { CtaSection } from '../components/sections/CtaSection';
import { CheckIcon, UsersIcon, LightbulbIcon, GlobeIcon, BookOpenIcon, AwardIcon, BriefcaseIcon } from 'lucide-react';
export function Solutions() {
  const solutions = [{
    title: 'Meetings & Retreats',
    description: 'Strategic gatherings that spark innovation and alignment while providing a refreshing change of environment.',
    outcomes: ['Increased team alignment', 'Accelerated decision making', 'Enhanced collaboration', 'Improved employee engagement'],
    activities: ['Strategy sprints', 'Executive cabanas', 'Beachside breakouts', 'Sunrise wellness sessions'],
    image: 'https://images.unsplash.com/photo-1517048676732-d65bc937f952?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2070&q=80'
  }, {
    title: 'Incentives',
    description: 'Reward top performers with unforgettable experiences that inspire future success and drive motivation.',
    outcomes: ['Boosted team morale', 'Increased retention', 'Motivation for future performance', 'Strengthened company culture'],
    activities: ['Luxury excursions', 'VIP experiences', 'Gala dinners', 'Adventure activities'],
    image: 'https://images.unsplash.com/photo-1544551763-46a013bb70d5?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2070&q=80'
  }, {
    title: 'Brand Launches',
    description: 'Create buzz and alignment around your new product or initiative with immersive brand experiences.',
    outcomes: ['Consistent messaging', 'Enthusiastic brand advocates', 'Memorable product experiences', 'Media-worthy moments'],
    activities: ['Immersive product demos', 'Media moments', 'Training workshops', 'Brand activation experiences'],
    image: 'https://images.unsplash.com/photo-1475721027785-f74eccf877e2?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2070&q=80'
  }, {
    title: 'Hybrid/Virtual Enhancements',
    description: 'Extend your in-person event to remote participants with engaging digital experiences.',
    outcomes: ['Inclusive participation', 'Extended reach', 'Content longevity', 'Cost-effective engagement'],
    activities: ['Livestreams', 'Virtual chat', 'Interactive polling', 'Digital breakout rooms'],
    image: 'https://images.unsplash.com/photo-1609921212029-bb5a28e60960?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2070&q=80'
  }];
  // New expert trainers data
  const expertCategories = [{
    title: 'Leadership Development',
    icon: <AwardIcon className="h-8 w-8 text-[#0099A1]" data-id="element-2559" />,
    description: 'Transform managers into inspiring leaders through immersive workshops and personalized coaching.',
    experts: ['Harvard Business School', 'Wharton', 'Stanford GSB']
  }, {
    title: 'Innovation & Design Thinking',
    icon: <LightbulbIcon className="h-8 w-8 text-[#0099A1]" data-id="element-2560" />,
    description: 'Unlock creative problem-solving with hands-on workshops led by innovation experts.',
    experts: ['IDEO Alumni', 'MIT Media Lab', 'Stanford d.school']
  }, {
    title: 'Strategic Planning',
    icon: <BriefcaseIcon className="h-8 w-8 text-[#0099A1]" data-id="element-2561" />,
    description: 'Develop actionable roadmaps with guidance from experienced strategy consultants and academics.',
    experts: ['McKinsey Alumni', 'London Business School', 'INSEAD']
  }, {
    title: 'Team Effectiveness',
    icon: <UsersIcon className="h-8 w-8 text-[#0099A1]" data-id="element-2562" />,
    description: 'Build high-performing teams through evidence-based frameworks and experiential learning.',
    experts: ['Google re:Work Trainers', 'Gallup Certified Coaches', 'Kellogg School']
  }];
  const services = [{
    title: 'Travel Logistics',
    items: ['Room blocks', 'Flight coordination', 'Ground transportation', 'VIP arrivals & departures']
  }, {
    title: 'Event Curation & Design',
    items: ['Custom agendas', 'Brand storytelling', 'Expert trainers', 'Theme development']
  }, {
    title: 'Production & Logistics',
    items: ['Venue consulting', 'Vendor management', 'On-site team', 'Risk management']
  }, {
    title: 'Event Tech',
    items: ['Custom apps', 'AV solutions', 'QR experiences', 'Virtual integrations']
  }, {
    title: 'Wellness & Community',
    items: ['Fitness activities', 'CSR programs', 'Beach clean-ups', 'Reef restoration']
  }];
  return <div className="pt-20 bg-[#F4E8D0]/30" data-id="element-2563">
      {/* Hero */}
      <section className="bg-[#0099A1] py-20" data-id="element-2564">
        <div className="container mx-auto px-4 md:px-6" data-id="element-2565">
          <div className="max-w-3xl" data-id="element-2566">
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-4" data-id="element-2567">
              Solutions
            </h1>
            <p className="text-xl text-white/90" data-id="element-2568">
              End-to-end event management tailored to your organization's unique
              goals and culture.
            </p>
          </div>
        </div>
      </section>

      {/* Solutions */}
      <section className="py-16 bg-white" data-id="element-2569">
        <div className="container mx-auto px-4 md:px-6" data-id="element-2570">
          <div className="space-y-24" data-id="element-2571">
            {solutions.map((solution, index) => <div key={index} className={`grid md:grid-cols-2 gap-12 items-center ${index % 2 === 1 ? 'md:grid-flow-dense' : ''}`} data-id="element-2572">
                <div className={index % 2 === 1 ? 'md:col-start-2' : ''} data-id="element-2573">
                  <h2 className="text-3xl font-bold text-[#374047] mb-4" data-id="element-2574">
                    {solution.title}
                  </h2>
                  <p className="text-lg text-[#374047]/80 mb-8" data-id="element-2575">
                    {solution.description}
                  </p>
                  <div className="mb-6" data-id="element-2576">
                    <h3 className="text-xl font-semibold text-[#0099A1] mb-4" data-id="element-2577">
                      Outcomes
                    </h3>
                    <ul className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-id="element-2578">
                      {solution.outcomes.map((outcome, i) => <li key={i} className="flex items-start" data-id="element-2579">
                          <CheckIcon className="h-5 w-5 text-[#0099A1] flex-shrink-0 mt-0.5" data-id="element-2580" />
                          <span className="ml-3 text-[#374047]" data-id="element-2581">{outcome}</span>
                        </li>)}
                    </ul>
                  </div>
                  <div data-id="element-2582">
                    <h3 className="text-xl font-semibold text-[#0099A1] mb-4" data-id="element-2583">
                      Typical Activities
                    </h3>
                    <ul className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-id="element-2584">
                      {solution.activities.map((activity, i) => <li key={i} className="flex items-start" data-id="element-2585">
                          <CheckIcon className="h-5 w-5 text-[#0099A1] flex-shrink-0 mt-0.5" data-id="element-2586" />
                          <span className="ml-3 text-[#374047]" data-id="element-2587">
                            {activity}
                          </span>
                        </li>)}
                    </ul>
                  </div>
                  <div className="mt-8" data-id="element-2588">
                    <Button variant="outline" to="/contact" data-id="element-2589">
                      Discuss Your {solution.title}
                    </Button>
                  </div>
                </div>
                <div className={index % 2 === 1 ? 'md:col-start-1' : ''} data-id="element-2590">
                  <div className="relative h-[400px] rounded-xl overflow-hidden shadow-lg" data-id="element-2591">
                    <img src={solution.image} alt={solution.title} className="w-full h-full object-cover" data-id="element-2592" />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" data-id="element-2593"></div>
                  </div>
                </div>
              </div>)}
          </div>
        </div>
      </section>

      {/* Expert Trainers Section - NEW */}
      <section className="py-16 bg-[#F4E8D0]/30" data-id="element-2594">
        <div className="container mx-auto px-4 md:px-6" data-id="element-2595">
          <div className="max-w-3xl mx-auto text-center mb-12" data-id="element-2596">
            <h2 className="text-3xl md:text-4xl font-bold text-[#374047] mb-4" data-id="element-2597">
              World-Class Expert Network
            </h2>
            <p className="text-lg text-[#374047]/80" data-id="element-2598">
              Elevate your corporate retreat with specialized training from top
              business school professors and industry experts.
            </p>
          </div>
          <div className="grid md:grid-cols-2 gap-12 items-center mb-16" data-id="element-2599">
            <div data-id="element-2600">
              <h3 className="text-2xl font-bold text-[#374047] mb-4" data-id="element-2601">
                From Boardroom to Classroom to Beach
              </h3>
              <p className="text-lg text-[#374047]/80 mb-6" data-id="element-2602">
                Our exclusive network of professors from the world's top
                business schools and corporate trainers brings unparalleled
                expertise to your retreat experience.
              </p>
              <p className="text-lg text-[#374047]/80 mb-6" data-id="element-2603">
                We carefully match expert facilitators to your specific
                organizational goals, creating custom learning experiences that
                drive real business outcomes.
              </p>
              <div className="space-y-4" data-id="element-2604">
                <div className="flex items-start" data-id="element-2605">
                  <CheckIcon className="h-5 w-5 text-[#0099A1] flex-shrink-0 mt-0.5" data-id="element-2606" />
                  <span className="ml-3 text-[#374047]" data-id="element-2607">
                    Expert-led workshops integrated into your retreat agenda
                  </span>
                </div>
                <div className="flex items-start" data-id="element-2608">
                  <CheckIcon className="h-5 w-5 text-[#0099A1] flex-shrink-0 mt-0.5" data-id="element-2609" />
                  <span className="ml-3 text-[#374047]" data-id="element-2610">
                    Immersive learning experiences in inspiring settings
                  </span>
                </div>
                <div className="flex items-start" data-id="element-2611">
                  <CheckIcon className="h-5 w-5 text-[#0099A1] flex-shrink-0 mt-0.5" data-id="element-2612" />
                  <span className="ml-3 text-[#374047]" data-id="element-2613">
                    Custom content development aligned with your objectives
                  </span>
                </div>
                <div className="flex items-start" data-id="element-2614">
                  <CheckIcon className="h-5 w-5 text-[#0099A1] flex-shrink-0 mt-0.5" data-id="element-2615" />
                  <span className="ml-3 text-[#374047]" data-id="element-2616">
                    Follow-up implementation support for lasting impact
                  </span>
                </div>
              </div>
            </div>
            <div data-id="element-2617">
              <img src="https://images.unsplash.com/photo-1524178232363-1fb2b075b655?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2070&q=80" alt="Expert-led workshop" className="rounded-xl shadow-lg" data-id="element-2618" />
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6" data-id="element-2619">
            {expertCategories.map((category, index) => <Card key={index} className="h-full" data-id="element-2620">
                <CardBody className="p-6" data-id="element-2621">
                  <div className="flex flex-col items-center text-center mb-6" data-id="element-2622">
                    <div className="mb-4 mt-2" data-id="element-2623">{category.icon}</div>
                    <h3 className="text-xl font-bold text-[#374047]" data-id="element-2624">
                      {category.title}
                    </h3>
                  </div>
                  <p className="text-[#374047]/80 mb-6" data-id="element-2625">
                    {category.description}
                  </p>
                  <div className="mt-auto" data-id="element-2626">
                    <h4 className="text-sm font-semibold text-[#0099A1] mb-2" data-id="element-2627">
                      Featured Experts From:
                    </h4>
                    <ul className="space-y-1" data-id="element-2628">
                      {category.experts.map((expert, i) => <li key={i} className="text-sm text-[#374047]" data-id="element-2629">
                          {expert}
                        </li>)}
                    </ul>
                  </div>
                </CardBody>
              </Card>)}
          </div>
          <div className="mt-12 text-center" data-id="element-2630">
            <Button variant="primary" to="/contact" data-id="element-2631">
              <BookOpenIcon className="h-5 w-5 mr-2" data-id="element-2632" />
              Explore Expert-Led Programs
            </Button>
          </div>
        </div>
      </section>

      {/* Services */}
      <section className="py-16 bg-[#F4E8D0]/50" data-id="element-2633">
        <div className="container mx-auto px-4 md:px-6" data-id="element-2634">
          <div className="max-w-3xl mx-auto text-center mb-12" data-id="element-2635">
            <h2 className="text-3xl md:text-4xl font-bold text-[#374047] mb-4" data-id="element-2636">
              Included Services
            </h2>
            <p className="text-lg text-[#374047]/80" data-id="element-2637">
              Every DestaMeet experience includes comprehensive support from our
              expert team.
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8" data-id="element-2638">
            {services.map((service, index) => <Card key={index} className="h-full" data-id="element-2639">
                <CardBody data-id="element-2640">
                  <h3 className="text-xl font-bold text-[#0099A1] mb-4" data-id="element-2641">
                    {service.title}
                  </h3>
                  <ul className="space-y-3" data-id="element-2642">
                    {service.items.map((item, i) => <li key={i} className="flex items-start" data-id="element-2643">
                        <CheckIcon className="h-5 w-5 text-[#0099A1] flex-shrink-0 mt-0.5" data-id="element-2644" />
                        <span className="ml-3 text-[#374047]" data-id="element-2645">{item}</span>
                      </li>)}
                  </ul>
                </CardBody>
              </Card>)}
          </div>
        </div>
      </section>

      <CtaSection data-id="element-2646" />
    </div>;
}